;(function (angular) {
   'use strict'

   angular.module('azure.data').factory('personData', personData)

   function personData($q, AzureAPI, CacheFactory, config, util) {
      //================================================================================
      // Cache
      //================================================================================

      const _cache = CacheFactory.createCache('people')

      // cache key variables, used for building cache keys (each should be unique)
      const _keyPerson = 0
      const _keyPersonAffiliateReferrals = 1
      const _keyOrderedPackagedProducts = 2

      const _localStorage = CacheFactory.createCache('localPeople', {
         storageMode: 'localStorage',
         maxAge: Number.MAX_VALUE,
      })

      // cache key variables, used for building cache keys (each should be unique)
      const _keyLocalPreference = 0

      const getCacheKey = util.joinArgs
      const retryOnce = util.asyncRetryOnce

      function cachePerson(person) {
         // Do not cache password fields
         delete person.password
         delete person['user-password']
         return _cache.put(getCacheKey(_keyPerson, person.id), person)
      }

      function cacheRemovePerson(personId) {
         _cache.remove(getCacheKey(_keyPerson, personId))
      }

      //================================================================================
      // Price Levels / Rewards Rate
      //================================================================================

      const _priceLevels = Object.freeze({
         retail: 'retail',
         wholesale: 'wholesale',
      })

      const _defaultRewardsRate = 2

      const _defaultPriceSettings = {
         priceLevel: _priceLevels.retail,
         rewardsRate: _defaultRewardsRate,
      }

      //================================================================================
      // Map App Options and Choices
      //================================================================================

      // Map app options (used for storing user preferences)
      const _mapAppOptions = Object.freeze({
         googleMaps: 'google-maps',
         appleMaps: 'apple-maps',
      })

      // Map app labels (used for display in the UI)
      function getMapAppLabel(option) {
         switch (option) {
            case _mapAppOptions.appleMaps:
               return 'Apple Maps'
            case _mapAppOptions.googleMaps:
               return 'Google Maps'
            default:
               console.warn(`Unsupported map app option: ${option}`)
         }
      }

      // Map app choices (used for rendering the UI)
      const _mapAppChoices = Object.freeze(
         Object.values(_mapAppOptions).map(function (option) {
            // Note that the properties here must match what's expected by `asKeyValueBoxEditUiHandler`
            return {
               value: option,
               label: getMapAppLabel(option),
            }
         })
      )

      const _defaultMapAppOption = _mapAppOptions.googleMaps

      //================================================================================
      // Local Storage Preferences (and helpers)
      //================================================================================

      // Some preferences that were previously used are now obsolete. Data for these
      // obsolete preferences may still exist in users' local storage. Reusing an
      // obsolete preference id for a new preference is not recommended, as it would
      // cause the new preference to inherit the stored value of the obsolete one.

      const _preferences = Object.freeze({
         // PREVIOUSLY USED; NOW OBSOLETE: 0
         // PREVIOUSLY USED; NOW OBSOLETE: 1
         preferListView: 2,
         // PREVIOUSLY USED; NOW OBSOLETE: 3
         organicOnly: 4,
         showAllSizes: 5,
         mapApp: 6,
      })

      //================================================================================
      // People
      //================================================================================

      function getPerson(personId, bypassCache) {
         const cacheKey = getCacheKey(_keyPerson, personId)

         let person = _cache.get(cacheKey)
         if (!person || bypassCache) {
            person = retryOnce(AzureAPI.person.get, {
               id: personId,
               inline:
                  'email,order-place-issue,is-drop-coordinator,homeDeliveryDriverDropIds,homeDeliveryDriverCreditsTotal,homeDeliveryDriverCreditsBreakdown',
            })
               .then(util.cleanItem)
               .then(cachePerson)

            // cache the promise
            _cache.put(cacheKey, person, {
               storeOnResolve: false,
            })
         }

         // $q.resolve ensures that this always returns a promise that resolves with the expected object
         return $q.resolve(person)
      }

      function getPersonIsGuest(personId) {
         if (!personId) {
            return $q.resolve(false)
         }
         return getPerson(personId).then(function (person) {
            return person.guest
         })
      }

      function getPeopleByIds(personIds) {
         return $q.map(personIds, function (personId) {
            return getPerson(personId)
         })
      }

      function savePerson(person) {
         return retryOnce(AzureAPI.person.save, person)
            .then(util.cleanItem)
            .then(function () {
               // Do not cache password fields
               delete person.password
               delete person['user-password']
               cachePerson(person)
               return person.id
            })
      }

      function updatePerson(personId, personData) {
         return getPerson(personId).then(function (person) {
            const updatedPerson = angular.extend({}, person, personData)
            // do not allow changing the id
            updatedPerson.id = personId
            return savePerson(updatedPerson)
         })
      }

      function updatePersonNotifications(personId, notificationsData) {
         return getPerson(personId).then(function (person) {
            const updatedPerson = angular.copy(person)
            updatedPerson.notifications = angular.extend({}, person.notifications, notificationsData)
            return savePerson(updatedPerson)
         })
      }

      //================================================================================
      // Ordered Packaged Products
      //================================================================================

      function getOrderedPackagedProducts(personId) {
         const cacheKey = getCacheKey(_keyOrderedPackagedProducts, personId)

         let orderedPackagedProducts = _cache.get(cacheKey)
         if (!orderedPackagedProducts) {
            orderedPackagedProducts = retryOnce(AzureAPI.person.orderedPackagedProducts, {
               id: personId,
            })

            // cache the promise, which is replaced with the resolved value by CacheFactory option storeOnResolve
            _cache.put(cacheKey, orderedPackagedProducts)
         }

         // $q.resolve ensures that this always returns a promise that resolves with the expected object
         return $q.resolve(orderedPackagedProducts)
      }

      //================================================================================
      // Affiliate
      //================================================================================

      function getAffiliateReferralsPage(personId, start, limit) {
         return retryOnce(AzureAPI['affiliate-referral'].query, {
            person: personId,
            limit: limit,
            start: start,
         })
      }

      function getAffiliateReferrals(personId, bypassCache) {
         const cacheKey = getCacheKey(_keyPersonAffiliateReferrals, personId)

         let affiliateReferrals = _cache.get(cacheKey)
         if (!affiliateReferrals || bypassCache) {
            affiliateReferrals = util.getPagesUntilEnd(
               getAffiliateReferralsPage.bind(null, personId),
               0,
               config.apiLimit
            )

            // cache the promise, which is replaced with the resolved value by CacheFactory option storeOnResolve
            _cache.put(cacheKey, affiliateReferrals)
         }

         // $q.resolve ensures that this always returns a promise that resolves with the expected object
         return $q.resolve(affiliateReferrals)
      }

      //================================================================================
      // Functions for getting and setting preferences
      //================================================================================

      function getPreferenceSync(personId, preference, defaultValue) {
         return _localStorage.get(getCacheKey(_keyLocalPreference, personId, preference)) ?? defaultValue
      }

      function getPreferenceAsync(personId, preference, defaultValue) {
         return $q.resolve(getPreferenceSync(personId, preference, defaultValue))
      }

      function updatePreference(personId, preference, value) {
         return $q.resolve(_localStorage.put(getCacheKey(_keyLocalPreference, personId, preference), value))
      }

      //================================================================================

      return {
         cachePerson: cachePerson,
         getPerson: getPerson,
         getPersonIsGuest: getPersonIsGuest,
         getPeopleByIds: getPeopleByIds,
         updatePerson: updatePerson,
         updatePersonNotifications: updatePersonNotifications,
         getPreferenceSync: getPreferenceSync,
         getPreferenceAsync: getPreferenceAsync,
         updatePreference: updatePreference,

         // Price Levels / Rewards Rate
         priceLevels: _priceLevels,
         defaultPriceSettings: _defaultPriceSettings,

         // Map App
         mapAppOptions: _mapAppOptions,
         mapAppChoices: _mapAppChoices,
         defaultMapAppOption: _defaultMapAppOption,

         // Ordered Packaged Products
         getOrderedPackagedProducts: getOrderedPackagedProducts,

         // Affiliate
         getAffiliateReferrals: getAffiliateReferrals,

         // Preferences
         preferences: _preferences,

         // cache clearing
         clear: _cache.removeAll,
         clearPerson: cacheRemovePerson,
      }
   }
})(angular)
